import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CountryCode, PostCheckoutSurveyOption } from './types';
import { getPostCheckoutSurveyOptions } from '../api';
import { Pencil } from '../assets/Pencil';
import { Spinner } from '../assets/Spinner';
import { Table } from '../component/Table';
import { FilterConfig, TableTools } from '../component/TableTools';
import { countryOptions } from '../dropdownOptions';
import { FilterParams } from '../hooks/useFetchWithPagination';

const headers = ['Name', 'Source', 'Bucket', 'Country', 'Status', ''];

const countryFilters: FilterConfig[] = [
  {
    key: 'country',
    label: 'Country',
    type: 'multiselect',
    options: countryOptions,
    placeholder: 'Select country',
  },
];

export const PostCheckoutSurveyOptions = () => {
  const [options, setOptions] = useState<PostCheckoutSurveyOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentFilters, setCurrentFilters] = useState<FilterParams>({});

  const fetchOptions = async () => {
    setIsLoading(true);
    try {
      const data = await getPostCheckoutSurveyOptions();
      setOptions(data);
    } catch (e) {
      console.error('Failed to fetch post-checkout survey options', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const searchBy = (text: string, _field: string) => {
    setSearchText(text);
  };

  const applyFilters = (newFilters: FilterParams, newSearchText?: string) => {
    setCurrentFilters(newFilters);
    if (newSearchText !== undefined) setSearchText(newSearchText);
  };

  const clearFilters = () => {
    setCurrentFilters({});
    setSearchText('');
  };

  const filteredOptions = useMemo(() => {
    const countryValues = Array.isArray(currentFilters.country)
      ? (currentFilters.country as string[])
      : [];
    return options.filter((o) => {
      const nameMatch =
        !searchText.trim() || o.label.toLowerCase().includes(searchText.trim().toLowerCase());
      const countryMatch = !countryValues.length || countryValues.includes(o.country);
      return nameMatch && countryMatch;
    });
  }, [options, searchText, currentFilters]);

  const mapOption = (option: PostCheckoutSurveyOption) => ({
    Name: option.label,
    Source: option.marketing_source ?? '--',
    Bucket: option.marketing_bucket ?? '--',
    Country: option.country === CountryCode.US ? '🇺🇸' : '🇨🇦',
    Status: option.status,
    '': (
      <div className="flex items-center justify-center gap-2">
        <Link to={`/post-checkout-survey-options/${option.id}/edit`}>
          <Pencil className="w-4 h-4 text-gray-500 hover:text-blue-500" />
        </Link>
      </div>
    ),
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="relative h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="flex items-center justify-between mb-3">
        <h1>How did you hear about Spot &amp; Tango?</h1>
      </div>
      <TableTools
        renderPagination={false}
        isLoading={isLoading}
        searchBy={searchBy}
        reFetch={fetchOptions}
        hasData={filteredOptions.length > 0}
        createLabel="Create New Option"
        changelogLink="/post-checkout-survey-options/changelog"
        createLink="/post-checkout-survey-options/create"
        currentSearchText={searchText}
        filters={countryFilters}
        applyFilters={applyFilters}
        clearFilters={clearFilters}
        currentFilters={currentFilters}
        autoApply
      >
        <div className="pb-6">
          <Table header={headers} data={filteredOptions.map(mapOption)} />
        </div>
      </TableTools>
    </div>
  );
};
